import React from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { CgWebsite } from "react-icons/cg";
import { FaGamepad } from "react-icons/fa";
import Particle from "../Particle";

import homeHero from "../../Assets/Projects/potbelly/home-hero.png";
import homeBento from "../../Assets/Projects/potbelly/home-bento.png";
import slopeHero from "../../Assets/Projects/potbelly/slope-hero.png";
import slopeWorlds from "../../Assets/Projects/potbelly/slope-worlds.png";
import slopeWiki from "../../Assets/Projects/potbelly/slope-wiki.png";
import pigCast from "../../Assets/Projects/potbelly/pig-cast.png";

const SITE = "https://calm-pebble-003d5cb1e.7.azurestaticapps.net";

const ss = { borderRadius: "8px", width: "100%", marginBottom: "10px" };
const label = { color: "rgb(155 126 172)", marginBottom: "6px", marginTop: "14px" };

function Section({ title, children }) {
  return (
    <>
      <Row style={{ justifyContent: "center", paddingTop: "30px" }}>
        <Col md={10}>
          <h4 style={{ color: "white", borderBottom: "1px solid rgb(100 60 140)", paddingBottom: "8px" }}>
            {title}
          </h4>
        </Col>
      </Row>
      {children}
    </>
  );
}

function Shot({ src, caption, md = 10 }) {
  return (
    <Row style={{ justifyContent: "center", paddingBottom: "6px" }}>
      <Col md={md}>
        <p style={label}>{caption}</p>
        <img src={src} alt={caption} className="img-fluid" style={ss} />
      </Col>
    </Row>
  );
}

function PotBellyPig() {
  return (
    <Container fluid className="project-section">
      <Container>
        <h1 className="project-heading">
          <strong className="purple">Pot Belly Pig Studios</strong>
        </h1>
        <p style={{ color: "white", textAlign: "center" }}>
          Pot Belly Pig Studios is a small indie game studio I created - brand, marketing site, and a
          flagship game. The site is an editorial, motion-driven React experience that introduces the
          studio and showcases its first title, <b>Slope Sliders</b>: an endless 3D icy descent
          starring a very determined penguin. It ships with a full in-app player guide, a cast of
          hand-built pixel-art pig mascots, and is deployed on Azure Static Web Apps.
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "14px",
            justifyContent: "center",
            paddingTop: "16px",
            paddingBottom: "26px",
          }}
        >
          <Button variant="primary" as="a" href={SITE} target="_blank" rel="noreferrer">
            <CgWebsite /> &nbsp;Visit the studio site
          </Button>
          <Button variant="primary" as="a" href={`${SITE}/slope-sliders`} target="_blank" rel="noreferrer">
            <FaGamepad /> &nbsp;Play Slope Sliders
          </Button>
        </div>

        <Row style={{ justifyContent: "center", paddingTop: "20px", paddingBottom: "10px", color: "white" }}>
          <Col md={10} className="project-card">
            <h4>What I Built</h4>
            <br />
            <p style={{ color: "white", textAlign: "left" }}>
              <ul>
                <li><b>The studio site</b> - an AIDA-structured, single-page React experience with a
                  GSAP-driven scroll story: scrubbed text reveals, floating mascots, a marquee, and a
                  gapless bento layout, all progressively enhanced so it renders fully without motion.</li>
                <li><b>Slope Sliders game page</b> - a dedicated page that sells the game: an animated
                  hero, a "five worlds, one slope" biome breakdown, and an embedded, fully written
                  player guide rendered from Markdown.</li>
                <li><b>A custom pixel-art pig cast</b> - one base pixel pig reskinned into a whole
                  roster of outfits (ski, wizard, pirate, king, ninja, and a WoW-themed armor set:
                  warrior, death knight, rogue, hunter, and more), generated from a reproducible
                  Python pipeline.</li>
                <li><b>A "pig bank"</b> - the home page pulls a random, distinct handful of pigs from
                  the roster into its scroll-gutter slots, so the cast you see changes on every visit.</li>
              </ul>
            </p>

            <h4>Built With</h4>
            <br />
            <p style={{ color: "white", textAlign: "left" }}>
              <ul>
                <li><b>React</b> + <b>React Router</b> for the multi-page single-page app</li>
                <li><b>GSAP</b> + <b>ScrollTrigger</b> for the scroll-scrubbed motion and reveals</li>
                <li><b>react-markdown</b> to render the player guide from a single source file</li>
                <li><b>Pillow</b> (Python) for the procedural pixel-pig generator and sprite export</li>
                <li><b>Azure Static Web Apps</b>, deployed straight from the CLI</li>
              </ul>
            </p>

            <h4>Why I Built It</h4>
            <br />
            <p style={{ color: "white", textAlign: "left" }}>
              I wanted a home for a game I was building and an excuse to design a brand around it from
              scratch - logo, mascots, voice, and a site that feels playful but intentional. Slope
              Sliders gave the studio a flagship, and the site gave the game a storefront, a guide,
              and a personality.
            </p>
          </Col>
        </Row>

        <Section title="The Studio Site">
          <Shot src={homeHero} caption="Home hero - the studio mascot, headline, and calls to action over a soft purple glow" />
          <Shot src={homeBento} caption="A gapless bento introduces the games; mascots drift through the page gutters, drawn at random from the pig bank" />
        </Section>

        <Section title="Slope Sliders - The Game Page">
          <Shot src={slopeHero} caption="The game's hero: an endless icy descent, with the app icon and quick links to play or read the guide" />
          <Shot src={slopeWorlds} caption="Five worlds, one slope - the mountain cycles through five biomes, each with its own look, music, and hazards" />
          <Shot src={slopeWiki} caption="A complete, in-page player guide rendered from Markdown, with a sticky on-this-page table of contents" />
        </Section>

        <Section title="A Hand-Built Pixel Pig Cast">
          <Shot src={pigCast} caption="One base pixel pig, many outfits - generated from a reproducible Python pipeline and rotated through the site at random" />
        </Section>

        <Particle />
      </Container>
    </Container>
  );
}

export default PotBellyPig;
